import { api } from '../api'
import type {
  AddColumnBody,
  AddColumnResult,
  AddSourceBody,
  AddSourceResult,
  AdvancePullResult,
  AdvanceTableResult,
  AlertBody,
  Capabilities,
  CreateTableBody,
  CreateTableResult,
  Deleted,
  DeleteColumnResult,
  DeleteSourceResult,
  DeleteTableResult,
  EstimateRunResult,
  GetPullResult,
  GetRowResult,
  GetTableResult,
  JsonObject,
  ListPullsResult,
  ListRowsResult,
  ListTablesResult,
  LookalikeBody,
  LookalikeResult,
  PreviewBody,
  PreviewColumnResult,
  Pricing,
  PromoteBody,
  PromoteRowsResult,
  ProspectPullPricing,
  ProspectPullStatus,
  Recipes,
  ReorderBody,
  ReorderColumnsResult,
  RunBody,
  RunSourceResult,
  RunTableResult,
  SaveAlertRuleResult,
  SaveRecipeBody,
  SaveRecipeResult,
  UpdateColumnBody,
  UpdateColumnResult,
  UpdateTableBody,
  UpdateTableResult,
} from '../types'

/**
 * Prospecting tables (BIBLE §15). One file per backend router, and this mirrors
 * `apps/api/routers/prospecting.py` plus the run log at `/pulls`.
 *
 * Two things here are load-bearing rather than incidental:
 *
 * `advanceTable` is a mutation the table page polls, and it is what makes a run finish at
 * all — the backend has no worker of its own between fifteen-minute sweeps. Its
 * invalidations are wide on purpose: a tick that advanced anything has just changed
 * cells, counts and run states together.
 *
 * `estimate`, `preview` and `lookalike` invalidate nothing. Two of them are questions
 * ("what would this cost", "what would this say") and the third proposes a search without
 * starting one, so none of them moves state a cached screen is holding.
 */

/** Only the parameters that were actually given, so a default is left to the backend. */
function params(entries: Record<string, string | number | boolean | undefined>) {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(entries)) {
    if (value === undefined) continue
    out[key] = String(value)
  }
  return out
}

const BASE = '/prospecting'

export interface ListRowsArgs {
  tableId: string
  filter?: string
  sort?: string
  descending?: boolean
  limit?: number
  offset?: number
}

export interface CsvSourceArgs {
  tableId: string
  file: File
  identifierColumn?: string
  name?: string
}

/**
 * The CSV upload, as a request object.
 *
 * Extracted from the endpoint so it can be tested: msw cannot round-trip a multipart
 * body under jsdom, and the two things worth pinning about this request are structural
 * rather than behavioural — which fields are on the form, and that **no `Content-Type`
 * of ours is set**. The multipart boundary is the browser's to write, and a header
 * without it is a body the server cannot parse.
 */
export function csvSourceRequest({ tableId, file, identifierColumn, name }: CsvSourceArgs) {
  const form = new FormData()
  form.append('file', file)
  if (identifierColumn) form.append('identifier_column', identifierColumn)
  if (name) form.append('name', name)
  return { url: `${BASE}/tables/${tableId}/sources/csv`, method: 'POST' as const, body: form }
}

export const prospectingApi = api.injectEndpoints({
  endpoints: (build) => ({
    // --- What this deployment can do, and what it charges for it
    getCapabilities: build.query<Capabilities, void>({
      query: () => `${BASE}/capabilities`,
      // Which provider keys the deployment was started with — so a button that could
      // only ever answer "not configured" is disabled with the backend's own sentence on
      // it rather than offered (BIBLE §15.10). Fixed for the life of the process on the
      // other side, so it is fetched once and never invalidated, like the prices below.
      keepUnusedDataFor: 3600,
    }),

    getPricing: build.query<Pricing, void>({
      query: () => `${BASE}/pricing`,
      // Constants: the rates, the search vocabularies, the model list and the formula
      // function signatures. Fetched rather than hard-coded so the console never carries
      // a second copy of the numbers that decide how much a click spends — but they do
      // not change between renders, so nothing invalidates this.
      keepUnusedDataFor: 3600,
    }),

    // --- Tables
    listTables: build.query<ListTablesResult, { clientId?: string | null } | void>({
      query: (args) => ({
        url: `${BASE}/tables`,
        params: args?.clientId ? { client_id: args.clientId } : {},
      }),
      providesTags: (result) => [
        { type: 'Table', id: 'LIST' },
        ...(result?.items ?? []).map((item) => ({ type: 'Table' as const, id: item.table.id })),
      ],
    }),

    getTable: build.query<GetTableResult, string>({
      query: (tableId) => `${BASE}/tables/${tableId}`,
      providesTags: (_result, _error, tableId) => [{ type: 'Table', id: tableId }],
    }),

    createTable: build.mutation<CreateTableResult, CreateTableBody>({
      query: (body) => ({ url: `${BASE}/tables`, method: 'POST', body }),
      invalidatesTags: [{ type: 'Table', id: 'LIST' }],
    }),

    updateTable: build.mutation<UpdateTableResult, { tableId: string; body: UpdateTableBody }>({
      // Omitted ≠ null: the caller sends only what changed, and `clear_spend_cap` is the
      // only way to remove a cap, because `spend_cap_usd: null` would be indistinguishable
      // from not mentioning it.
      query: ({ tableId, body }) => ({ url: `${BASE}/tables/${tableId}`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: 'Table', id: tableId },
        { type: 'Table', id: 'LIST' },
      ],
    }),

    deleteTable: build.mutation<DeleteTableResult, string>({
      query: (tableId) => ({ url: `${BASE}/tables/${tableId}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, tableId) => [
        { type: 'Table', id: tableId },
        { type: 'Table', id: 'LIST' },
      ],
    }),

    // --- Sources
    addSource: build.mutation<AddSourceResult, { tableId: string; body: AddSourceBody }>({
      // **Spends money** for `apify_search`. A `table` source is absorbed in the request
      // and comes back with a run already `succeeded`.
      query: ({ tableId, body }) => ({
        url: `${BASE}/tables/${tableId}/sources`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: 'Table', id: tableId },
        { type: 'TableRows', id: tableId },
        { type: 'Pull', id: 'LIST' },
      ],
    }),

    addCsvSource: build.mutation<AddSourceResult, CsvSourceArgs>({
      query: csvSourceRequest,
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: 'Table', id: tableId },
        { type: 'TableRows', id: tableId },
        { type: 'Pull', id: 'LIST' },
      ],
    }),

    runSource: build.mutation<
      RunSourceResult,
      { tableId: string; sourceId: string; config?: JsonObject }
    >({
      // **Spends money** for `apify_search`. `config`, when given, replaces the source's
      // saved filters before the run — "run again" reconfirms rather than repeats.
      query: ({ tableId, sourceId, config }) => ({
        url: `${BASE}/tables/${tableId}/sources/${sourceId}/run`,
        method: 'POST',
        body: config ? { config } : undefined,
      }),
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: 'Table', id: tableId },
        { type: 'TableRows', id: tableId },
        { type: 'Pull', id: 'LIST' },
      ],
    }),

    deleteSource: build.mutation<DeleteSourceResult, { tableId: string; sourceId: string }>({
      query: ({ tableId, sourceId }) => ({
        url: `${BASE}/tables/${tableId}/sources/${sourceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: 'Table', id: tableId },
        { type: 'TableRows', id: tableId },
      ],
    }),

    // --- Columns
    addColumn: build.mutation<AddColumnResult, { tableId: string; body: AddColumnBody }>({
      query: ({ tableId, body }) => ({
        url: `${BASE}/tables/${tableId}/columns`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: 'Table', id: tableId },
        { type: 'TableRows', id: tableId },
      ],
    }),

    updateColumn: build.mutation<
      UpdateColumnResult,
      { tableId: string; columnId: string; body: UpdateColumnBody }
    >({
      query: ({ tableId, columnId, body }) => ({
        url: `${BASE}/tables/${tableId}/columns/${columnId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: 'Table', id: tableId },
        { type: 'TableRows', id: tableId },
      ],
    }),

    deleteColumn: build.mutation<DeleteColumnResult, { tableId: string; columnId: string }>({
      query: ({ tableId, columnId }) => ({
        url: `${BASE}/tables/${tableId}/columns/${columnId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: 'Table', id: tableId },
        { type: 'TableRows', id: tableId },
      ],
    }),

    reorderColumns: build.mutation<
      ReorderColumnsResult,
      { tableId: string; body: ReorderBody }
    >({
      // Every column id, in the new order. Refused whole when a drop breaks the
      // left-only rule, which is why the console restores its previous order on a 409
      // rather than trying to work out which move survived.
      query: ({ tableId, body }) => ({
        url: `${BASE}/tables/${tableId}/columns/order`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: 'Table', id: tableId },
        { type: 'TableRows', id: tableId },
      ],
    }),

    previewColumn: build.mutation<
      PreviewColumnResult,
      { tableId: string; body: PreviewBody }
    >({
      // A question, not a change — except with `run: true`, which spends one call and
      // still changes nothing a cached screen is holding.
      query: ({ tableId, body }) => ({
        url: `${BASE}/tables/${tableId}/preview`,
        method: 'POST',
        body,
      }),
    }),

    // --- Rows
    listRows: build.query<ListRowsResult, ListRowsArgs>({
      query: ({ tableId, filter, sort, descending, limit, offset }) => ({
        url: `${BASE}/tables/${tableId}/rows`,
        // One encoded parameter for the filter: it is a formula, and carries quotes,
        // braces and spaces.
        params: params({ filter, sort, descending, limit, offset }),
      }),
      providesTags: (_result, _error, { tableId }) => [{ type: 'TableRows', id: tableId }],
    }),

    getRow: build.query<GetRowResult, { tableId: string; rowId: string }>({
      query: ({ tableId, rowId }) => `${BASE}/tables/${tableId}/rows/${rowId}`,
      // Tagged by the table rather than the row, so a retry that refills one cell
      // refreshes the drawer that asked for it.
      providesTags: (_result, _error, { tableId }) => [{ type: 'TableRows', id: tableId }],
    }),

    // --- Runs
    estimateRun: build.mutation<EstimateRunResult, { tableId: string; body: RunBody }>({
      // The dry run. Same body as the real one, so what is quoted is what would be spent.
      query: ({ tableId, body }) => ({
        url: `${BASE}/tables/${tableId}/estimate`,
        method: 'POST',
        body,
      }),
    }),

    runTable: build.mutation<RunTableResult, { tableId: string; body: RunBody }>({
      query: ({ tableId, body }) => ({ url: `${BASE}/tables/${tableId}/run`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: 'Table', id: tableId },
        { type: 'Pull', id: 'LIST' },
      ],
    }),

    runColumn: build.mutation<
      RunTableResult,
      { tableId: string; columnId: string; body: RunBody }
    >({
      query: ({ tableId, columnId, body }) => ({
        url: `${BASE}/tables/${tableId}/columns/${columnId}/run`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: 'Table', id: tableId },
        { type: 'Pull', id: 'LIST' },
      ],
    }),

    advanceTable: build.mutation<AdvanceTableResult, string>({
      // Polled by the table page while anything is unfinished. Nothing else moves a run
      // except the backend's fifteen-minute sweep, so this call is the run's engine.
      query: (tableId) => ({ url: `${BASE}/tables/${tableId}/advance`, method: 'POST' }),
      invalidatesTags: (result, _error, tableId) => [
        { type: 'Table', id: tableId },
        { type: 'TableRows', id: tableId },
        { type: 'Pull', id: 'LIST' },
        ...(result?.runs ?? []).map((run) => ({ type: 'Pull' as const, id: run.id })),
      ],
    }),

    // --- Promotion and lookalike
    promoteRows: build.mutation<PromoteRowsResult, { tableId: string; body: PromoteBody }>({
      // Leads land `imported`, so the campaign's own screens change even though nothing
      // has been scheduled — and every row's `contacted_before` may have moved.
      query: ({ tableId, body }) => ({
        url: `${BASE}/tables/${tableId}/promote`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { tableId, body }) => [
        { type: 'TableRows', id: tableId },
        { type: 'Campaign', id: body.campaign_id },
        { type: 'CampaignStats', id: body.campaign_id },
        { type: 'Lead', id: body.campaign_id },
      ],
    }),

    lookalike: build.mutation<LookalikeResult, { tableId: string; body: LookalikeBody }>({
      // Spends one model call and *proposes* a search. Nothing runs until the operator
      // adds the proposal as a source, so there is nothing to invalidate.
      query: ({ tableId, body }) => ({
        url: `${BASE}/tables/${tableId}/lookalike`,
        method: 'POST',
        body,
      }),
    }),

    // --- Recipes
    listRecipes: build.query<Recipes, void>({
      query: () => `${BASE}/recipes`,
      providesTags: [{ type: 'Recipe', id: 'LIST' }],
    }),

    saveRecipe: build.mutation<SaveRecipeResult, { recipeId?: string; body: SaveRecipeBody }>({
      query: ({ recipeId, body }) =>
        recipeId
          ? { url: `${BASE}/recipes/${recipeId}`, method: 'PUT', body }
          : { url: `${BASE}/recipes`, method: 'POST', body },
      invalidatesTags: [{ type: 'Recipe', id: 'LIST' }],
    }),

    deleteRecipe: build.mutation<Deleted, string>({
      query: (recipeId) => ({ url: `${BASE}/recipes/${recipeId}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Recipe', id: 'LIST' }],
    }),

    newTableFromRecipe: build.mutation<
      CreateTableResult,
      { recipeId: string; body: CreateTableBody }
    >({
      query: ({ recipeId, body }) => ({
        url: `${BASE}/recipes/${recipeId}/tables`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Table', id: 'LIST' }],
    }),

    // --- Alert rules
    saveAlertRule: build.mutation<
      SaveAlertRuleResult,
      { tableId: string; alertId?: string; body: AlertBody }
    >({
      query: ({ tableId, alertId, body }) =>
        alertId
          ? { url: `${BASE}/tables/${tableId}/alerts/${alertId}`, method: 'PUT', body }
          : { url: `${BASE}/tables/${tableId}/alerts`, method: 'POST', body },
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: 'Table', id: tableId },
        { type: 'Table', id: 'LIST' },
        { type: 'TableRows', id: tableId },
      ],
    }),

    deleteAlertRule: build.mutation<Deleted, { tableId: string; alertId: string }>({
      query: ({ tableId, alertId }) => ({
        url: `${BASE}/tables/${tableId}/alerts/${alertId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: 'Table', id: tableId },
        { type: 'Table', id: 'LIST' },
        { type: 'TableRows', id: tableId },
      ],
    }),

    markAlertViewed: build.mutation<
      SaveAlertRuleResult,
      { tableId: string; alertId: string }
    >({
      query: ({ tableId, alertId }) => ({
        url: `${BASE}/tables/${tableId}/alerts/${alertId}/viewed`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: 'Table', id: tableId },
        { type: 'Table', id: 'LIST' },
      ],
    }),

    // --- The run log
    listPulls: build.query<
      ListPullsResult,
      { status?: ProspectPullStatus; limit?: number; offset?: number } | void
    >({
      query: (args) => ({
        url: '/pulls',
        params: params({ status: args?.status, limit: args?.limit, offset: args?.offset }),
      }),
      providesTags: (result) => [
        { type: 'Pull', id: 'LIST' },
        ...(result?.items ?? []).map((pull) => ({ type: 'Pull' as const, id: pull.id })),
      ],
    }),

    getPull: build.query<GetPullResult, string>({
      query: (id) => `/pulls/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Pull', id }],
    }),

    getPullPricing: build.query<ProspectPullPricing, void>({
      query: () => '/pulls/pricing',
      keepUnusedDataFor: 3600,
    }),

    advancePull: build.mutation<AdvancePullResult, string>({
      // The single-run poller the run page uses. A run belonging to a table also moves
      // that table's rows along, which is why the table tag goes in the moment the
      // result names one.
      query: (id) => ({ url: `/pulls/${id}/advance`, method: 'POST' }),
      invalidatesTags: (result, _error, id) => [
        { type: 'Pull', id },
        { type: 'Pull', id: 'LIST' },
        ...(result?.pull.table_id
          ? [
              { type: 'Table' as const, id: result.pull.table_id },
              { type: 'TableRows' as const, id: result.pull.table_id },
            ]
          : []),
      ],
    }),
  }),
})

export const {
  useGetCapabilitiesQuery,
  useGetPricingQuery,
  useListTablesQuery,
  useGetTableQuery,
  useCreateTableMutation,
  useUpdateTableMutation,
  useDeleteTableMutation,
  useAddSourceMutation,
  useAddCsvSourceMutation,
  useRunSourceMutation,
  useDeleteSourceMutation,
  useAddColumnMutation,
  useUpdateColumnMutation,
  useDeleteColumnMutation,
  useReorderColumnsMutation,
  usePreviewColumnMutation,
  useListRowsQuery,
  useGetRowQuery,
  useEstimateRunMutation,
  useRunTableMutation,
  useRunColumnMutation,
  useAdvanceTableMutation,
  usePromoteRowsMutation,
  useLookalikeMutation,
  useListRecipesQuery,
  useSaveRecipeMutation,
  useDeleteRecipeMutation,
  useNewTableFromRecipeMutation,
  useSaveAlertRuleMutation,
  useDeleteAlertRuleMutation,
  useMarkAlertViewedMutation,
  useListPullsQuery,
  useGetPullQuery,
  useGetPullPricingQuery,
  useAdvancePullMutation,
} = prospectingApi
